import React, {useEffect, useState, useCallback, useRef} from 'react';
import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import {useDispatch, useSelector} from "react-redux";
import { store } from "react-notifications-component";
import styles from './tag-customer-form.module.scss';
import TextField from "../FormElements/TextField";
import ButtonFilled from "../FormElements/ButtonFilled";
import {useFormik} from "formik";
import branchApi from "../../store/branchStore/branchApi";
import profileApi from "../../store/profileStore/profileApi";
import {PhoneInputField} from "../PhoneInput";
import Select from "react-dropdown-select";
import * as yup from 'yup';
import GroupConfigWarning from "../GroupConfigWarning";

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  content: {
    maxWidth: '425px',
    width: '100%',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    border: 'none',
    padding: 32,
    borderRadius: 24,
  }
};

const TagCustomerFormModal = (props) => {
  const { t } = useTranslation();
  const { info } = useSelector(state => state.branchReducer);
  const dispatch = useDispatch()
  const tags = useSelector(state => state?.branchReducer?.info?.configuration?.tags) || []
  const [client, setClient] = useState(null)
  const [isFetchingClient, setIsFetchingClient] = useState(false)
  const phoneDebounceRef = useRef(null)
  const formikRef = useRef(null)

  const {
    userId,
    isOpen,
    setIsOpen,
    setUserId,
    users,
    handleSuccessFormSubmit,
  } = props;

  const onModalClose = () => {
    setUserId('')
    setIsOpen(false)
    formik.resetForm()
    setClient(null)
  }

  const formik = useFormik({
    initialValues: {
      fio: '',
      phone: '',
      tag_ids: []
    },
    validationSchema: yup.object({
      phone: yup.string().required(t("Form.requiredField"))
    }),
    onSubmit: (async (values) => {
      try {
        if (info.id) {
          if (userId) {
            await branchApi.putBranchTagCustomer(info.id,  userId,{
              tag_ids: values.tag_ids.map(({ id }) => id),
            })
          } else {
            await branchApi.postBranchTagCustomer(info.id, {
              fio: values.fio,
              phone: values.phone,
              tag_ids: values.tag_ids.map(({ id }) => id),
            })
          }
        }

        handleSuccessFormSubmit()
      } catch (e) {
        const res = e.response ? e.response.data : null;

        store.addNotification({
          title: `Error code: ${res ? res.code : 'NULL'}`,
          message: `${res ? res.message : t('Notify.notRespond')}`,
          type: "danger",
          insert: "top",
          container: "top-right",
          animationIn: ["animate__animated", "animate__fadeIn"],
          animationOut: ["animate__animated", "animate__fadeOut"],
          dismiss: {
            duration: 5000,
            onScreen: true
          }
        });
      }
    })
  });

  // Keep formik ref up to date so async callbacks can access the latest instance
  formikRef.current = formik;

  useEffect(() => {
    if (userId && users.length) {
      const foundUser = users.find((user) => user.id === userId);

      if (foundUser) {
        formik.setValues({
          fio: `${foundUser.first_name} ${foundUser.last_name}`,
          phone: foundUser.phone,
          tag_ids: foundUser.user_tags.map((tag) => ({ label: tag.name, value: tag.id, ...tag })),
        })
      } else {
        setUserId('')
        setIsOpen(false)
      }
    }
  }, [userId, users]);

  const handlePhoneLookup = useCallback((phone) => {
    if (!phone || phone.length < 10 || !info?.id) return;

    if (phoneDebounceRef.current) {
      clearTimeout(phoneDebounceRef.current);
    }

    phoneDebounceRef.current = setTimeout(async () => {
      try {
        setIsFetchingClient(true);
        const formattedPhone = phone.startsWith('+') ? phone : '+' + phone;
        const res = await profileApi.postCheckPhone({ phone: formattedPhone });
        const userData = res?.data;
        if (userData?.exists) {
          const fioValue = [userData.first_name, userData.last_name].filter(Boolean).join(' ');
          formikRef.current.setFieldValue('fio', fioValue);
          setClient({ id: userData.user_id, first_name: userData.first_name, last_name: userData.last_name, phone: userData.phone });

          // Try to get user's existing tags for this branch
          try {
            const pbRes = await branchApi.getCustomerPrivateBranch(info.id, userData.user_id);
            const userTags = pbRes?.data?.tags || [];
            if (userTags.length > 0) {
              formikRef.current.setFieldValue('tag_ids', userTags.map((tag) => ({ label: tag.name, value: tag.id, ...tag })));
            }
          } catch (_) {
            // Tags lookup is non-critical
          }
        } else {
          setClient(null);
        }
      } catch (e) {
        setClient(null);
      } finally {
        setIsFetchingClient(false);
      }
    }, 500);
  }, [info?.id])

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (phoneDebounceRef.current) {
        clearTimeout(phoneDebounceRef.current);
      }
    };
  }, [])

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      style={modalStyles}
      onRequestClose={onModalClose}
    >
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" onClick={onModalClose} style={{ zIndex: 2, position: 'absolute', right: '12px', top: '12px', cursor: 'pointer' }}>
        <rect x="28.7266" y="8.72949" width="3.52711" height="28.2169" rx="1.76356" transform="rotate(45 28.7266 8.72949)" fill="#AFAFAB"/>
        <rect x="8.77344" y="11.2236" width="3.52711" height="28.2169" rx="1.76356" transform="rotate(-45 8.77344 11.2236)" fill="#AFAFAB"/>
      </svg>
      <div className={styles.modal}>
        <div className={styles.container}>
          {userId ? (
            <h3
              style={{
                fontFamily: 'graphic, sans',
                fontSize: '22px',
                textAlign: 'center',
                marginBottom: '2rem'
              }}
            >
              {t('edit')}
            </h3>
          ) : (
            <h3
              style={{
                fontFamily: 'graphic, sans',
                fontSize: 22,
                textAlign: 'center',
                marginBottom: '2rem'
              }}
            >
              {t('addClient')}
            </h3>
          )}
          <GroupConfigWarning branch={info} variant="inline">
            {t('groupWarning.tagCustomer', { count: info?.group_branch_count ?? 0 })}
          </GroupConfigWarning>
          <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
            <div className={styles.block}>
               <span
                 style={{
                   fontFamily: 'graphic, sans-serif',
                   fontSize: 16,
                   lineHeight: '24px',
                   color: '#1D1F23',
                   display: 'inline-block',
                   marginBottom: '6px'
                 }}
               >
                  {t('NSCorporative.phone')}
                </span>
              <PhoneInputField
                inputStyle={{ height: '46px' }}
                containerStyle={{ width: '100%' }}
                enableSearch={false}
                required
                disabled={!!userId}
                disableDropdown={true}
                size="large"
                value={formik.values.phone}
                onChange={(v) => {
                  formik.setFieldValue('phone', v);
                  if (!userId) {
                    handlePhoneLookup(v);
                  }
                }}
              />
              {formik.touched.phone && formik.errors.phone && (
                <span style={{ fontSize: 12, color: 'tomato', display: 'inline-block', marginTop: '3px' }}>{formik.errors.phone}</span>
              )}
            </div>

            <div className={styles.block}>
              <TextField
                label={t('NSTag.clientFullName')}
                value={formik.values.fio}
                required
                disabled={!!userId}
                onChange={(e) => formik.setFieldValue('fio', e.target.value)}
              />
            </div>

            <div>
              <span
                style={{
                  fontFamily: 'graphic, sans-serif',
                  fontSize: 16,
                  lineHeight: '24px',
                  color: '#1D1F23',
                }}
              >
                {t('NSTag.tags')}
              </span>

              {/*{formik.values.title && (*/}
              {/*  <BadgeTag*/}
              {/*    style={{*/}
              {/*      ...(formik.values.backgroundColor ? {backgroundColor: formik.values.backgroundColor} : {}),*/}
              {/*      ...(formik.values.color ? {color: formik.values.color} : {}),*/}
              {/*    }}*/}
              {/*  >*/}
              {/*    {formik.values.title}*/}
              {/*  </BadgeTag>*/}
              {/*)}*/}
            </div>

            <div className={styles.block}>
              <Select
                placeholder={t('NSTag.tagChoose')}
                options={tags?.map((tag) => ({ label: tag.name, value: tag.id, ...tag })) || []}
                style={{ minHeight: 45, outline: 'none' }}
                required
                values={formik.values.tag_ids}
                multi
                // itemRenderer={({item, props, methods}) => (
                //   <div
                //     {...props}
                //     {...methods}
                //     className={styles.item}
                //     style={{ background: item.background_color, color: item.color }}
                //   >
                //     {}
                //     {item.label}
                //   </div>
                // )}
                onChange={(v) => {
                  formik.setFieldValue('tag_ids', v)
                }}
                dropdownHeight="150px"
              />
            </div>

            <div className={styles.footer}>
              <ButtonFilled style={{ width: '100%' }} type="submit" loading={formik.isSubmitting || isFetchingClient}>{t('save')}</ButtonFilled>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  )
}

export default TagCustomerFormModal;